<script setup>
// FLOW-05 (correção de 14/10): o teclado e a semântica de uma lista de escolha, sem mudar o desenho
// nem o mouse de quem já usa o componente (a paginação das tabelas, o filtro de conversas e a regra
// do CSAT). O gatilho abre a lista (`aria-haspopup="listbox"`, `aria-expanded`), e o foco vai para
// ela (`role="listbox"`, com a opção sob o teclado em `aria-activedescendant`):
// - ↓ e ↑ andam pelas opções, Home e End vão às pontas, e a opção sob o teclado acende como no hover
//   (só no teclado: aberta pelo mouse, a lista fica como era, sem nenhuma linha acesa);
// - Enter (ou espaço) escolhe, fecha e devolve o foco ao gatilho;
// - Esc fecha e devolve o foco ao gatilho, sem fechar a caixa ou o popover em volta;
// - Tab segue para o próximo campo e fecha a lista; Shift+Tab volta ao gatilho.
// Com o gatilho fechado e no foco, ↓ e ↑ também abrem a lista.
//
// O DOM e as classes são os de antes (`DropdownContainer`, `DropdownSection` e `DropdownItem`),
// escritos aqui: a lista precisa do `role="listbox"` no `ul` e das opções sem o papel de item de
// lista no meio, e esses componentes de base não deixam pôr atributo no `ul` nem no `li`. O
// `DropdownBody`, que desenha o painel, continua o de base. O slot `trigger` recebe, além do
// `toggle`, o `isOpen` e os `triggerAttrs` (id, aria e o teclado), para um gatilho próprio.
//
// PowerFlow (16/10, o agente do teste): `searchable` põe uma busca no alto da lista aberta, para
// a lista longa. O foco vai para ela, e não para a lista; digitar filtra pelo rótulo e acende a
// primeira opção que sobra, e as setas, o Enter, o Esc e o Tab seguem os da lista. O espaço, o
// Home e o End ficam com o texto. Quem não pede continua como era.
import { computed, nextTick, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementBounding, useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import Icon from 'next/icon/Icon.vue';

import Button from 'next/button/Button.vue';

// [{label, icon, value}]
const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
  hideIcon: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'faded',
  },
  label: {
    type: String,
    default: null,
  },
  // FLOW-05 L9 (task 9.3): a lista da largura do gatilho, como a de um `<select>`. Sem isto ela
  // tem a largura do conteúdo dentro de um bloco de largura zero, e a opção longa quebra em
  // várias linhas mesmo com espaço ao lado. Quem não pede continua como era.
  matchTriggerWidth: {
    type: Boolean,
    default: false,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const selected = defineModel({
  type: [String, Number],
  required: true,
});

const rootRef = ref(null);
const dropdownRef = ref(null);
const listRef = ref(null);
const searchRef = ref(null);
const isOpen = ref(false);
// O texto da busca (`searchable`), que volta vazio a cada vez que a lista abre.
const query = ref('');
// A opção sob o teclado (o índice em `options`), ou -1. Ela só acende quando a lista é do teclado
// (aberta por ele, ou depois de uma tecla): aberta pelo clique, a lista é a de antes.
const activeIndex = ref(-1);
const byKeyboard = ref(false);
// O gatilho que abriu a lista dá o nome dela (o id dele, lido ao abrir: um gatilho próprio pode
// trazer o id de quem o usa, como o `for` de um `<label>`).
const labelledBy = ref(null);

const uid = useId();
const triggerId = `filter-select-${uid}`;
const listId = `${triggerId}-lista`;
const optionId = index => `${listId}-${index}`;

const { top } = useElementBounding(rootRef);
const { height } = useWindowSize();
const { height: dropdownHeight } = useElementBounding(dropdownRef);

const selectedOption = computed(() => {
  return props.options?.find(o => o.value === selected.value) || {};
});

const iconToRender = computed(() => {
  if (props.hideIcon) return null;
  return selectedOption.value.icon || 'i-lucide-chevron-down';
});

const dropdownPosition = computed(() => {
  const DROPDOWN_MAX_HEIGHT = 340;
  // Get actual height if available or use default
  const menuHeight = dropdownHeight.value
    ? dropdownHeight.value + 20
    : DROPDOWN_MAX_HEIGHT;
  const spaceBelow = height.value - top.value;
  return spaceBelow < menuHeight ? 'bottom-0' : 'top-0';
});

// As opções da lista aberta: todas, ou as que a busca acha pelo rótulo (o título sai na busca).
const visibleOptions = computed(() => {
  const term = query.value.trim().toLowerCase();
  if (!props.searchable || !term) return props.options || [];
  return (props.options || []).filter(
    option =>
      !option.disabled && String(option.label).toLowerCase().includes(term)
  );
});

// As opções que se escolhem (a desabilitada é um título e o teclado a pula), pelo índice.
const selectableIndexes = computed(() =>
  visibleOptions.value.flatMap((option, index) =>
    option.disabled ? [] : [index]
  )
);

const triggerElement = () =>
  rootRef.value?.querySelector('[aria-haspopup="listbox"]');

const showActive = () =>
  nextTick(() =>
    listRef.value
      ?.querySelector(`[data-index="${activeIndex.value}"]`)
      ?.scrollIntoView?.({ block: 'nearest' })
  );

function open({ keyboard = false } = {}) {
  byKeyboard.value = keyboard;
  query.value = '';
  const current = props.options.findIndex(
    option => !option.disabled && option.value === selected.value
  );
  activeIndex.value =
    current >= 0 ? current : (selectableIndexes.value[0] ?? -1);
  labelledBy.value = triggerElement()?.id || null;
  isOpen.value = true;
  nextTick(() => {
    (props.searchable ? searchRef : listRef).value?.focus({
      preventScroll: true,
    });
    showActive();
  });
}

function close({ focusTrigger = false } = {}) {
  if (!isOpen.value) return;
  isOpen.value = false;
  activeIndex.value = -1;
  query.value = '';
  if (focusTrigger) nextTick(() => triggerElement()?.focus());
}

// Digitar na busca acende a primeira opção que sobra: o Enter a escolhe.
watch(query, () => {
  if (!isOpen.value) return;
  byKeyboard.value = true;
  activeIndex.value = selectableIndexes.value[0] ?? -1;
  showActive();
});

// O clique que o Enter ou o espaço fazem no botão chega com `detail` 0; o do mouse, com 1 ou mais.
const toggle = event =>
  isOpen.value ? close() : open({ keyboard: event?.detail === 0 });

const updateSelected = index => {
  const option = visibleOptions.value[index];
  if (!option || option.disabled) return;
  selected.value = option.value;
  close({ focusTrigger: true });
};

function onTriggerKeydown(event) {
  if (isOpen.value || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
  event.preventDefault();
  open({ keyboard: true });
}

function onListKeydown(event) {
  const indexes = selectableIndexes.value;
  const position = indexes.indexOf(activeIndex.value);
  const moveTo = {
    ArrowDown: () => indexes[Math.min(position + 1, indexes.length - 1)],
    ArrowUp: () => indexes[Math.max(position - 1, 0)],
    Home: () => indexes[0],
    End: () => indexes[indexes.length - 1],
  }[event.key];
  if (moveTo) {
    event.preventDefault();
    byKeyboard.value = true;
    activeIndex.value = moveTo() ?? -1;
    showActive();
    return;
  }
  if (['Enter', ' '].includes(event.key)) {
    event.preventDefault();
    updateSelected(activeIndex.value);
    return;
  }
  if (event.key === 'Escape') {
    // A caixa do passo e os popovers também fecham no Esc: este é só da lista.
    event.preventDefault();
    event.stopPropagation();
    close({ focusTrigger: true });
    return;
  }
  if (event.key === 'Tab' && event.shiftKey) {
    event.preventDefault();
    close({ focusTrigger: true });
    return;
  }
  // ← e → não são da lista, e na caixa do passo levariam ao passo vizinho com a lista aberta.
  if (['ArrowLeft', 'ArrowRight'].includes(event.key)) event.preventDefault();
}

// Na busca, o espaço, o Home, o End, o ← e o → são do texto; o resto é o teclado da lista.
function onSearchKeydown(event) {
  if ([' ', 'Home', 'End', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
    return;
  }
  onListKeydown(event);
}

// O Tab leva o foco para o campo seguinte: a lista fecha quando o foco sai dela para fora do
// componente. Para o próprio gatilho (o clique nele), quem fecha é o `toggle`.
function onListFocusout(event) {
  if (!rootRef.value?.contains(event.relatedTarget)) close();
}

const triggerAttrs = computed(() => ({
  id: triggerId,
  'aria-haspopup': 'listbox',
  'aria-expanded': isOpen.value,
  'aria-controls': isOpen.value ? listId : undefined,
  onKeydown: onTriggerKeydown,
}));
</script>

<template>
  <div
    ref="rootRef"
    v-on-click-outside="() => close()"
    class="relative space-y-2"
  >
    <slot
      name="trigger"
      :toggle="toggle"
      :is-open="isOpen"
      :trigger-attrs="triggerAttrs"
    >
      <Button
        type="button"
        sm
        slate
        :variant
        :icon="iconToRender"
        :trailing-icon="selectedOption.icon ? false : true"
        :label="label || (hideLabel ? null : selectedOption.label)"
        v-bind="triggerAttrs"
        @click="toggle"
      />
    </slot>
    <div
      v-if="isOpen"
      class="absolute"
      :class="{ 'inset-x-0': matchTriggerWidth }"
    >
      <DropdownBody
        ref="dropdownRef"
        class="min-w-56 z-50"
        :class="[dropdownPosition, { 'w-full': matchTriggerWidth }]"
        strong
      >
        <div v-if="searchable" class="relative">
          <Icon
            class="absolute size-4 left-2 top-2 text-n-slate-11"
            icon="i-lucide-search"
          />
          <input
            ref="searchRef"
            v-model="query"
            type="text"
            role="combobox"
            autocomplete="off"
            spellcheck="false"
            aria-autocomplete="list"
            aria-expanded="true"
            :aria-controls="listId"
            :aria-activedescendant="
              activeIndex >= 0 ? optionId(activeIndex) : undefined
            "
            :aria-label="searchPlaceholder || t('COMBOBOX.SEARCH_PLACEHOLDER')"
            :placeholder="searchPlaceholder || t('COMBOBOX.SEARCH_PLACEHOLDER')"
            class="reset-base w-full h-8 py-1.5 pl-8 pr-2 m-0 text-sm border-0 rounded-lg bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-10 outline-none"
            data-busca
            @keydown="onSearchKeydown"
            @focusout="onListFocusout"
          />
        </div>
        <div class="-mx-2 n-dropdown-section [&>ul]:max-h-72">
          <ul
            :id="listId"
            ref="listRef"
            role="listbox"
            tabindex="-1"
            :aria-labelledby="labelledBy || undefined"
            :aria-activedescendant="
              activeIndex >= 0 ? optionId(activeIndex) : undefined
            "
            class="gap-2 grid reset-base list-none px-2 overflow-y-auto max-h-96 outline-none"
            @keydown="onListKeydown"
            @focusout="onListFocusout"
          >
            <li
              v-if="searchable && !visibleOptions.length"
              role="presentation"
              class="px-2 py-1.5 text-sm text-n-slate-11"
              data-busca-vazia
            >
              {{ t('COMBOBOX.EMPTY_STATE') }}
            </li>
            <template
              v-for="(option, index) in visibleOptions"
              :key="option.value"
            >
              <li
                v-if="option.disabled"
                :id="optionId(index)"
                role="option"
                aria-disabled="true"
                aria-selected="false"
                class="px-2 py-1.5 text-xs font-medium text-n-slate-10 select-none"
              >
                {{ option.label }}
              </li>
              <li v-else class="n-dropdown-item" role="none">
                <div
                  :id="optionId(index)"
                  role="option"
                  :aria-selected="option.value === selected"
                  :data-index="index"
                  :data-value="option.value"
                  class="flex text-left rtl:text-right items-center p-2 reset-base text-sm text-n-slate-12 w-full border-0 hover:bg-n-alpha-2 rounded-lg gap-3"
                  :class="{
                    'bg-n-alpha-2': byKeyboard && index === activeIndex,
                  }"
                  @click="updateSelected(index)"
                  @mousemove="activeIndex = index"
                >
                  <Icon
                    v-if="option.icon"
                    class="size-4 text-n-slate-11"
                    :icon="option.icon"
                  />
                  {{ option.label }}
                </div>
              </li>
            </template>
          </ul>
        </div>
      </DropdownBody>
    </div>
  </div>
</template>
